import { atom, useAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
    ACTIVE_SUB_KEY,
    CREDITS_KEY,
    PROFILE_KEY,
    USER_INFO_KEY,
    SUBSCRIPTION_KEY,
    PLANS_KEY,
    DEVICES_KEY,
} from "@/AppConstants";
import { createSessionAtom } from "./persistedAtom";
import {
    TRACKING_PARAM_KEYS,
    setTrackingParam,
    getTrackingParam,
} from "@/utils/trackingStore";

export const USER_ATOM = createSessionAtom(USER_INFO_KEY);

export const PROFILE_ATOM = createSessionAtom(PROFILE_KEY);

export const ACTIVESUBSCRIPTIONS_ATOM = createSessionAtom(ACTIVE_SUB_KEY);

export const SUBSCRIPTION_ATOM = createSessionAtom(SUBSCRIPTION_KEY);

export const PLANS_ATOM = createSessionAtom(PLANS_KEY);

export const CREDITS_ATOM = createSessionAtom(CREDITS_KEY);

export const DEVICES_ATOM = createSessionAtom(DEVICES_KEY);

export const SUBSCRIPT_INFO_ATOM = atom();

export const ALL_CHAT_MESSAGE_ATOM = atom();

export const VOICEMAIL_ATOM = atom();

export const CALL_LOG_ATOM = atom();

export const VOICE_MAIL_ATOM = atom();

export const BALANCE_ATOM = atom();

export const ESIM_ATOM = atom();

export const ESIM_PLANS_ATOM = atom();

export const BLOCK_NUMBER_ATOM = atom([]);

const GCLID_STORE = atom();
export const GCLID_ATOM = atom(
    (get) => get(GCLID_STORE),
    (get, set, gclid) => {
        set(GCLID_STORE, gclid);
    }
);

const FBCLID_STORE = atom();
export const FBCLID_ATOM = atom(
    (get) => get(FBCLID_STORE),
    (get, set, fbclid) => {
        set(FBCLID_STORE, fbclid);
    }
);

export const useTrackingParams = () => {
    const location = useLocation();
    const [gclid, setGclid] = useAtom(GCLID_ATOM);
    const [fbclid, setFbclid] = useAtom(FBCLID_ATOM);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        // Capture every known click identifier into the in-memory store so
        // genClidUrl can re-stamp them onto every funnel navigation URL.
        for (const key of TRACKING_PARAM_KEYS) {
            setTrackingParam(key, params.get(key));
        }

        const urlGclid = params.get("gclid");
        const urlFbclid = params.get("fbclid");

        if (urlGclid) {
            setGclid(urlGclid);
        }
        if (urlFbclid) {
            setFbclid(urlFbclid);
        }
    }, [location.search]);

    return useMemo(() => {
        const params = new URLSearchParams(location.search);
        const urlGclid = params.get("gclid");
        const urlFbclid = params.get("fbclid");

        // gclid/fbclid keep their explicit named fields (consumers destructure
        // them); the rest are exposed too, sourced from the URL or the store, so
        // any event/payload that wants them can read them without extra wiring.
        const extras = {};
        for (const key of TRACKING_PARAM_KEYS) {
            if (key === "gclid" || key === "fbclid") continue;
            extras[key] = params.get(key) || getTrackingParam(key) || null;
        }

        return {
            ...extras,
            gclid: urlGclid || gclid || null,
            fbclid: urlFbclid || fbclid || null,
        };
    }, [location.search, gclid, fbclid]);
};
